import React from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { Box, LinearProgress } from '@mui/material';
import { DataGrid, GridColDef, GridRowSelectionModel } from '@mui/x-data-grid';
import {
  S3File,
  prefixState,
  selectModelsState,
  selectCocoonsState,
  selectTiresState,
  selectWheelhousesState,
  allModelsState,
  allCocoonsState,
  allTiresState,
  allWheelhousesState,
  allModelsLoadingState,
  allCocoonsLoadingState,
  allTiresLoadingState,
  allWheelhousesLoadingState,
  allMorphedState,
  selectMorphedState,
  allMorphedLoadingState
} from 'features/file-browser/state/fileAtoms';
import { CustomTypography } from 'shared/ui/CustomTypography';
import { customBoxSx, customDataGridSx } from './utils/customSx';

const columns: GridColDef[] = [
  { field: 'name', headerName: 'Name', flex: 1.5 },
  { field: 'lastModified', headerName: 'Last Modified', flex: 1 },
  { field: 'size', headerName: 'Size', flex: 0.5 },
];

export const FilesDataGrid: React.FC = () => {
  const prefix = useRecoilValue(prefixState);

  const allModels = useRecoilValue(allModelsState);
  const allCocoons = useRecoilValue(allCocoonsState);
  const allTires = useRecoilValue(allTiresState);
  const allWheelhouses = useRecoilValue(allWheelhousesState);
  const allMorphed = useRecoilValue(allMorphedState);

  const setSelectModels = useSetRecoilState(selectModelsState);
  const setSelectCocoons = useSetRecoilState(selectCocoonsState);
  const setSelectTires = useSetRecoilState(selectTiresState);
  const setSelectWheelhouses = useSetRecoilState(selectWheelhousesState);
  const setSelectMorphed = useSetRecoilState(selectMorphedState);

  const modelLoading = useRecoilValue(allModelsLoadingState);
  const cocoonLoading = useRecoilValue(allCocoonsLoadingState);
  const tireLoading = useRecoilValue(allTiresLoadingState);
  const wheelhouseLoading = useRecoilValue(allWheelhousesLoadingState);
  const morphedLoading = useRecoilValue(allMorphedLoadingState);

  const handleSelectionChange = (selectionModel: GridRowSelectionModel) => {
    const selectedRows = selectionModel
      .map((id) => {
        switch (prefix) {
          case "model":
            return allModels.find((_, index) => index === id);
          case "cocoon":
            return allCocoons.find((_, index) => index === id);
          case "tire":
            return allTires.find((_, index) => index === id);
          case "wheelhouse":
            return allWheelhouses.find((_, index) => index === id);
          case "morphed":
            return allMorphed.find((_, index) => index === id);
          default:
            return undefined;
        }
      })
      .filter((file) => file !== undefined) as S3File[];

    switch (prefix) {
      case "model":
        setSelectModels(selectedRows);
        break;
      case "cocoon":
        setSelectCocoons(selectedRows);
        break;
      case "tire":
        setSelectTires(selectedRows);
        break;
      case "wheelhouse":
        setSelectWheelhouses(selectedRows);
        break;
      case "morphed":
        setSelectMorphed(selectedRows);
        break;
    }
  };

  const rows = (() => {
    switch (prefix) {
      case "model":
        return allModels.map((file, index) => ({ id: index, ...file }));
      case "cocoon":
        return allCocoons.map((file, index) => ({ id: index, ...file }));
      case "tire":
        return allTires.map((file, index) => ({ id: index, ...file }));
      case "wheelhouse":
        return allWheelhouses.map((file, index) => ({ id: index, ...file }));
      case "morphed":
        return allMorphed.map((file, index) => ({ id: index, ...file }));
      default:
        return [];
    }
  })();

  const loading = (() => {
    switch (prefix) {
      case "model":
        return modelLoading;
      case "cocoon":
        return cocoonLoading;
      case "tire":
        return tireLoading;
      case "wheelhouse":
        return wheelhouseLoading;
      case "morphed":
        return morphedLoading;
      default:
        return false;
    }
  })();

  if (loading) {
    return (
      <Box component="div" sx={customBoxSx}>
        <LinearProgress />
      </Box>
    );
  }

  return rows.length === 0 ? (
    <Box component="div" sx={customBoxSx}>
      <CustomTypography>No files found</CustomTypography>
    </Box>
  ) : (
    <DataGrid
      rows={rows}
      columns={columns}
      initialState={{
        pagination: { paginationModel: { page: 0, pageSize: 10 } },
      }}
      pageSizeOptions={[1, 5, 10]}
      checkboxSelection
      autoHeight
      sx={customDataGridSx}
      style={{ width: '100%' }}
      onRowSelectionModelChange={handleSelectionChange}
      hideFooterSelectedRowCount={true}
    />
  );
};
